import { defineComponent, h, ref, computed, onUnmounted, onMounted } from 'vue';
import { button, col, cols } from '@/ui/components';
import { Icon } from '@/ui/components/icon/Icon';
import { checklistTemplateCreateModal } from '@/ui/components/checklistTemplate/checklistCreateTemplateModal/ChecklistTemplateCreateModal';
import { ChecklistTemplateEditable } from '@/ui/DTO';
import {
  checklists,
  addChecklist,
  removeChecklist,
  undeletedChecklists,
  updateChecklist
} from '@/ui/pages/checklist/ChecklistBuilderChecklistTemplateStore';
import { useCurrentRoute } from '@/ui/routes';
import { useTask } from '@/ui/concerns';
import { intoEditable, list } from '@/services/api/resources/checklistTemplate';
import { t } from '@/ui/i18n/i18n';
import { FeatureNames, newFeature } from '@/ui/components/NewFeature';
import { EventBus } from '@/ui/EventBus';
import { Events } from '@/ui/events';

export const ChecklistTemplates = defineComponent({
  name: 'ChecklistTemplates',
  setup() {
    const currentRoute = useCurrentRoute();

    const isEditing = computed(() => !!currentRoute.value.params.id);

    const isModalOpen = ref(false);

    const selectedActionEdit = ref<ChecklistTemplateEditable | null>(null);
    const isEditingAction = computed(() => selectedActionEdit.value !== null);

    const toggleModal = (value: boolean) => {
      return () => {
        EventBus.$emit(Events.viewedNewFeature, FeatureNames.ADD_ACTION_TO_CHECKLIST);

        isModalOpen.value = value;
        selectedActionEdit.value = null;
      };
    };

    const onSave = (checklistTemplate: ChecklistTemplateEditable) => {
      if (isEditingAction.value) {
        updateChecklist(checklistTemplate);
      } else {
        addChecklist(checklistTemplate);
      }

      isModalOpen.value = false;
    };

    const onEditClick = (resource: ChecklistTemplateEditable) => {
      selectedActionEdit.value = resource;
      isModalOpen.value = true;
    };

    const resetChecklists = () => checklists.value = [];

    const checklistTemplateListQuery = computed(() => ({
      page: 1,
      perPage: 50,
      checklist: currentRoute.value.params.id ? [parseInt(currentRoute.value.params.id)] : [],
    }));

    const getChecklistTemplates = useTask(
      () => list(checklistTemplateListQuery.value),
      response => {
        checklists.value = response.data.map(intoEditable);
      }
    );

    const fetchChecklistTemplates = () => {
      if (isEditing.value) {
        getChecklistTemplates.run();
      }
    };

    onUnmounted(resetChecklists);
    onMounted(fetchChecklistTemplates);

    return function render() {
      return h('div', [
        checklistTemplateCreateModal({
          props: {
            active: isModalOpen.value,
            checklistTemplate: selectedActionEdit.value as ChecklistTemplateEditable,
          },
          on: { close: toggleModal(false), save: onSave }
        }),
        newFeature({ featureName: FeatureNames.ADD_ACTION_TO_CHECKLIST, classes: 'mb-4' }),
        h('p', { class: 'checklist-builder__second-stage-title' }, t('checklist-template.inspection')),
        h('div', { class: 'text-sm text-70 mb-4' }, t('checklist-template.description')),
        undeletedChecklists.value.map(checklistTemplate => cols([
          col([
            h('div', checklistTemplate.title)
          ]),
          col([
            h(Icon, {
              class: 'mr-2 cursor-pointer has-text-grey',
              props: {
                icon: 'edit',
                size: '1.5x',
              },
              on: {
                click: () => onEditClick(checklistTemplate),
              }
            }),
            h(Icon, {
              class: 'cursor-pointer has-text-grey',
              props: {
                icon: 'trash-alt',
                size: '1.5x',
              },
              on: {
                click: () => removeChecklist(checklistTemplate)
              }
            }),
          ], true)
        ])),
        button(
          undeletedChecklists.value.length > 0
            ? t('checklist-template.add-another-action')
            : t('checklist-template.add-action'),
          toggleModal(true),
          undefined,
          { outlined: true }
        ),
      ]);
    };
  },
});
